// TODO: Remove after UILib component is added
<template>
  <div class="UIShareableLinkPreview" role="group" :aria-label="ariaLabel">
    <div class="UIShareableLinkPreview__row">
      <div class="UIShareableLinkPreview__main">
        <div class="UIShareableLinkPreview__icon-wrap" aria-hidden="true">
          <UIIcon class="UIShareableLinkPreview__icon" name="linkExternal" />
        </div>
        <div class="UIShareableLinkPreview__text">
          <span class="UIShareableLinkPreview__title">{{ heading }}</span>
          <span class="UIShareableLinkPreview__url" :title="url">
            {{ url }}
          </span>
        </div>
      </div>
      <UIButton
        type="button"
        theme="secondary"
        size="sm"
        class="UIShareableLinkPreview__copy"
        @click="onCopy"
      >
        <template v-if="copied" #icon-before>
          <UIIcon
            class="UIShareableLinkPreview__copy-icon"
            name="checkRegular"
          />
        </template>
        {{ copied ? 'Copied' : copyLabel }}
      </UIButton>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { onBeforeUnmount, ref } from 'vue'

import { UIButton, UIIcon } from '@foundever/ui-lib'
import { useClipboard } from '@vueuse/core'

import { useToast } from '@/store/toast'

const props = withDefaults(
  defineProps<{
    url: string
    heading?: string
    copyLabel?: string
    ariaLabel?: string
  }>(),
  {
    heading: 'Shareable URL',
    copyLabel: 'Copy link',
    ariaLabel: 'Shareable link',
  },
)

const { copy } = useClipboard()

const copied = ref(false)
let copiedResetTimer: ReturnType<typeof setTimeout> | undefined

async function onCopy() {
  if (!props.url) return
  try {
    await copy(props.url)
    copied.value = true
    clearTimeout(copiedResetTimer)
    copiedResetTimer = setTimeout(() => (copied.value = false), 2000)
  } catch {
    useToast().onError('Could not copy link')
  }
}

onBeforeUnmount(() => clearTimeout(copiedResetTimer))
</script>

<style scoped lang="scss">
.UIShareableLinkPreview {
  min-width: 0;
  padding: var(---fds-global-space-sm) var(---fds-global-space-sm)
    var(---fds-global-space-sm) var(---fds-global-space-md);
  background: var(--bg-tertiary, #fafafa);
  border-left: 2px solid var(--interactive-border-default, #d9d9de);
  border-radius: 8px;
}

.UIShareableLinkPreview__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(---fds-global-space-sm);
  width: 100%;
  min-width: 0;
}

.UIShareableLinkPreview__main {
  display: flex;
  align-items: center;
  gap: var(---fds-global-space-md);
  min-width: 0;
  flex: 1;
}

.UIShareableLinkPreview__icon-wrap {
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--border-heavy);
  border-radius: 8px;
  background: var(--bg-elevated-primary, var(--bg-primary));
}

.UIShareableLinkPreview__icon {
  --size: 1rem;
  --color: var(--icon-secondary);
}

.UIShareableLinkPreview__text {
  display: flex;
  flex-direction: column;
  gap: var(---fds-global-space-xxs);
  align-items: flex-start;
  min-width: 0;
  flex: 1;
}

.UIShareableLinkPreview__title {
  @include font-body-small-medium;
  color: var(--text-primary);
  white-space: nowrap;
}

.UIShareableLinkPreview__url {
  display: block;
  width: 100%;
  min-width: 0;
  margin: 0;
  @include font-body-small-regular;
  color: var(--text-secondary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.UIShareableLinkPreview__copy {
  flex-shrink: 0;
  border-radius: 0.25rem;
  border: 1px solid var(--border-heavy);
  color: var(--text-primary);
}

.UIShareableLinkPreview__copy-icon {
  --size: 0.875rem;
  --color: var(--icon-primary);
}
</style>
